// Vista compartida de "lista de sesiones agrupadas por fecha" usada por
// SesionesKine (kine, cross-paciente) y el tab Sesiones de DetallePaciente
// (kine, single-paciente). Sprint 3 B4b — extract del código pre-existente
// en SesionesKine.tsx para dedupe (cierra el TODO comment "consolidar
// cuando aparezca el 4to uso": HistorialSesiones + SesionCompletada +
// SesionesKine + tab Sesiones = 4).
//
// Modos de interacción:
//   - Interactivo: pasar `onItemClick`. Row renderea como <button> con
//     hover + cursor pointer.
//   - No-op: omitir `onItemClick`. Row renderea como <div> sin hover ni
//     cursor.
//
// Prop `showPatientName` controla si el nombre del paciente aparece en
// cada row. true (default) para SesionesKine (contexto cross-paciente),
// false para DetallePaciente (paciente ya en el contexto de la pantalla).

import { formatHora, formatDuration, qualityColorClass } from "@/lib/utils";

export type SessionListEstado = "completada" | "cancelada" | "interrumpida";

export interface SessionListItem {
  id: string;
  started_at: string;
  ejercicio_nombre: string;
  paciente_id: string;
  paciente_nombre: string;
  total_reps: number;
  score_promedio: number | null;
  duration_sec: number | null;
  estado: SessionListEstado;
}

const ESTADO_BADGE: Record<
  SessionListEstado,
  { label: string; className: string }
> = {
  completada: {
    label: "Completada",
    className:
      "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  },
  interrumpida: {
    label: "Interrumpida",
    className:
      "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  },
  cancelada: {
    label: "Cancelada",
    className:
      "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
  },
};

export interface SessionListGroupedProps {
  sesiones: SessionListItem[];
  /** Handler opcional del click. Si no se pasa, los rows no son
   *  clickeables (renderea <div> en lugar de <button>) — modo no-op. */
  onItemClick?: (sesion: SessionListItem) => void;
  /** Si true, muestra el nombre del paciente como línea principal del
   *  row. Default true (SesionesKine cross-paciente). En contextos donde
   *  el paciente ya es conocido (tab Sesiones de DetallePaciente), pasar
   *  false. */
  showPatientName?: boolean;
}

export function SessionListGrouped({
  sesiones,
  onItemClick,
  showPatientName = true,
}: SessionListGroupedProps) {
  const groups = groupByDay(sesiones);
  const interactive = onItemClick !== undefined;

  return (
    <div className="space-y-6">
      {groups.map(([dayKey, items]) => (
        <div key={dayKey}>
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">
            {formatGroupLabel(dayKey)}
          </p>
          <div className="space-y-2">
            {items.map((s) => (
              <SessionRow
                key={s.id}
                sesion={s}
                onClick={
                  interactive ? () => onItemClick(s) : undefined
                }
                showPatientName={showPatientName}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Row ──────────────────────────────────────────────────────────────────

function SessionRow({
  sesion,
  onClick,
  showPatientName,
}: {
  sesion: SessionListItem;
  onClick: (() => void) | undefined;
  showPatientName: boolean;
}) {
  const badge = ESTADO_BADGE[sesion.estado];
  const hora = formatHora(sesion.started_at);
  const duracion =
    sesion.duration_sec !== null ? formatDuration(sesion.duration_sec) : null;
  const qualityPct =
    sesion.score_promedio !== null
      ? Math.round(sesion.score_promedio * 100)
      : null;
  const qualityClass = qualityColorClass(qualityPct);

  const commonInner = (
    <>
      <div className="w-12 shrink-0 text-sm font-mono tabular-nums text-muted-foreground">
        {hora}
      </div>

      <div className="flex-1 min-w-0">
        {showPatientName ? (
          <>
            <p className="text-sm font-semibold truncate">
              {sesion.paciente_nombre}
            </p>
            <p className="text-xs text-muted-foreground truncate">
              {sesion.ejercicio_nombre}
              {sesion.total_reps > 0 && ` · ${sesion.total_reps} reps`}
              {duracion && ` · ${duracion}`}
            </p>
          </>
        ) : (
          <>
            <p className="text-sm font-semibold truncate">
              {sesion.ejercicio_nombre}
            </p>
            <p className="text-xs text-muted-foreground truncate">
              {sesion.total_reps > 0 && `${sesion.total_reps} reps`}
              {sesion.total_reps > 0 && duracion && ` · `}
              {duracion && duracion}
            </p>
          </>
        )}
      </div>

      <div className="flex items-center gap-3 shrink-0">
        {qualityPct !== null && (
          <span className={`text-lg font-bold tabular-nums ${qualityClass}`}>
            {qualityPct}%
          </span>
        )}
        <span
          className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.className}`}
        >
          {badge.label}
        </span>
      </div>
    </>
  );

  if (onClick) {
    return (
      <button
        onClick={onClick}
        className="w-full flex items-center gap-4 rounded-xl border border-border bg-card px-4 py-3 hover:bg-muted/30 text-left transition-colors"
      >
        {commonInner}
      </button>
    );
  }

  // No-op mode: div sin hover ni cursor. Visualmente idéntico al row
  // interactivo salvo por la ausencia de affordance de click.
  return (
    <div className="w-full flex items-center gap-4 rounded-xl border border-border bg-card px-4 py-3">
      {commonInner}
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────

/** Agrupa sesiones por día (YYYY-MM-DD) preservando el orden desc del
 *  query original. */
function groupByDay(
  sesiones: SessionListItem[],
): Array<[string, SessionListItem[]]> {
  const map = new Map<string, SessionListItem[]>();
  for (const s of sesiones) {
    const dayKey = s.started_at.slice(0, 10);
    if (!map.has(dayKey)) map.set(dayKey, []);
    map.get(dayKey)!.push(s);
  }
  return [...map.entries()];
}

/** "HOY" / "AYER" / "VIE 1 MAY" según antigüedad. Usado solo para los
 *  headers de grupo — los items individuales muestran solo la hora. */
function formatGroupLabel(dayKey: string): string {
  const fecha = new Date(dayKey + "T12:00:00");
  const hoy = new Date();
  const ayer = new Date(hoy);
  ayer.setDate(hoy.getDate() - 1);

  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  if (sameDay(fecha, hoy)) return "HOY";
  if (sameDay(fecha, ayer)) return "AYER";

  return fecha
    .toLocaleDateString("es-AR", {
      weekday: "short",
      day: "numeric",
      month: "short",
    })
    .toUpperCase();
}
